
import React, { useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface NavGroupProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
}

const NavGroup = ({ title, isExpanded, onToggle, children, className = '' }: NavGroupProps) => {
  const headerRef = useRef<HTMLDivElement>(null);

  // Create a handler for the header click that prevents event propagation
  const handleHeaderClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={onToggle}
      className={className}
      data-state={isExpanded ? "open" : "closed"}
    >
      <div 
        ref={headerRef}
        className="flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-muted/50 transition-colors rounded-md"
        onClick={handleHeaderClick}
        role="button"
        aria-expanded={isExpanded}
        aria-controls={`nav-group-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground/60">
          {title}
        </h4>
        <CollapsibleTrigger asChild>
          <button 
            className="h-5 w-5 rounded-md hover:bg-muted flex items-center justify-center text-foreground/60"
            onClick={(e) => {
              // Stop propagation to prevent double toggling
              e.stopPropagation();
            }}
            aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            <ChevronRight 
              className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
              aria-hidden="true"
            />
          </button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent 
        className="ml-1 space-y-1 mt-1"
        id={`nav-group-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default NavGroup;
